/**
 * The request headers a hub accepts from a cross-origin client — the hub's own
 * CORS allow-list, mirrored here as ONE list (card `t_68d110ee`).
 *
 * Why this is not cosmetic: the hub's middleware names a fixed set of headers
 * (`Access-Control-Allow-Headers`, `otto/src/server/middleware/index.ts`), and a
 * browser refuses to send a request that carries anything else — the failure is
 * a *preflight rejection inside the WebView*:
 *
 *     Access to fetch at 'http://<hub>/api/auth/refresh' from origin
 *     'http://localhost' has been blocked by CORS policy: Request header field
 *     x-skip-interceptor is not allowed by Access-Control-Allow-Headers in
 *     preflight
 *
 * The request never reaches the hub, so the hub logs nothing and the app reads
 * it as a dead session: that is how the local connect's sliding-session refresh
 * died, and with it the silent re-login behind it.
 *
 * Rules:
 *   1. a hub-bound request may carry only headers named here;
 *   2. a header a hub route genuinely needs is added to the hub's middleware
 *      FIRST, then here — the two lists are one contract, and only the hub can
 *      make a browser send it;
 *   3. a refused header is dropped and reported rather than sent, because
 *      sending it is a request that never leaves the browser.
 *
 * Pure: no globals, no browser needed to test it.
 */

/** Exactly the hub's `Access-Control-Allow-Headers`, lower-cased. */
export const HUB_CORS_ALLOWED_HEADERS: readonly string[] = [
  'content-type',
  'authorization',
  'x-requested-with',
  // Every otto-ui build since the first commit sends this on the sliding-session
  // refresh and nothing reads it; the hub names it so devices that cannot be
  // updated still refresh. New requests must not add it back — see (2).
  'x-skip-interceptor',
];

/** Headers the browser itself owns; they are never set by app code. */
const BROWSER_OWNED = new Set(['origin', 'referer', 'host', 'content-length']);

/** Header names (lower-cased) in `headers` that a hub would refuse at preflight. */
export function refusedHubCorsHeaders(headers: Record<string, string | undefined> | null | undefined): string[] {
  const refused: string[] = [];
  for (const name of Object.keys(headers || {})) {
    const lower = name.toLowerCase();
    if (BROWSER_OWNED.has(lower)) continue;
    if (!HUB_CORS_ALLOWED_HEADERS.includes(lower) && !refused.includes(lower)) {
      refused.push(lower);
    }
  }
  return refused;
}

/**
 * The headers to actually send to a hub: everything allowed, minus the ones a
 * preflight would reject. `refused` names what was dropped so the caller can say
 * so — a silently dropped header is a bug nobody sees.
 */
export function sanitizeHubRequestHeaders(headers: Record<string, string>): {
  headers: Record<string, string>;
  refused: string[];
} {
  const refused = refusedHubCorsHeaders(headers);
  if (refused.length === 0) return { headers, refused };
  const kept: Record<string, string> = {};
  for (const [name, value] of Object.entries(headers)) {
    if (!refused.includes(name.toLowerCase())) kept[name] = value;
  }
  return { headers: kept, refused };
}
